var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import React, { useMemo, useCallback, useState, useRef } from 'react';
import { isArray, isEmpty } from '../_utils/lang';
import { createPrefixCls } from '../_utils/string';
import { DATA_TYPE } from './constants';
import PickerDate from './picker-date';
import PickerDateRange from './picker-date-range';
import PickerMulti from './picker-multi';
import PickerSingle from './picker-single';
import PickerTree from './picker-tree';
import { getDataType, getFixedData, convertTextToRecord, convertDate, initDateYear } from './utils';
const prefixCls = createPrefixCls('picker');
const useGetCols = ({ type, data, defaultValue, dateRange, dateCols, step, onChange }) => {
    const cols = useMemo(() => {
        if (type === DATA_TYPE.SINGLE) {
            return (React.createElement(PickerSingle, { data: data, defaultValue: defaultValue, onChange: onChange }));
        }
        else if (type === DATA_TYPE.MULTI || type === DATA_TYPE.TIME) {
            return (React.createElement(PickerMulti, { className: classnames(type === DATA_TYPE.TIME && prefixCls('cols-time')), data: data, defaultValue: defaultValue, onChange: onChange }));
        }
        else if (type === DATA_TYPE.TREE || type === DATA_TYPE.REGION) {
            return (React.createElement(PickerTree, { data: data, defaultValue: defaultValue, onChange: onChange }));
        }
        else if (type === DATA_TYPE.DATE) {
            return (React.createElement(PickerDate, { defaultValue: defaultValue, dateRange: dateRange, dateCols: dateCols, step: step, onChange: onChange }));
        }
        else if (type === DATA_TYPE.DATE_RANGE) {
            return (React.createElement(PickerDateRange, { defaultValue: defaultValue, dateRange: dateRange, dateCols: dateCols, step: step, onChange: onChange }));
        }
    }, [type, data, defaultValue, onChange, dateRange, dateCols, step]);
    return cols;
};
export const PickerView = (_a) => {
    var { title, allowClear = false, data, defaultValue, dateRange, dateCols = ['year', 'month', 'day'], step, onCancel, onChange, onClear, onConfirm } = _a, restProps = __rest(_a, ["title", "allowClear", "data", "defaultValue", "dateRange", "dateCols", "step", "onCancel", "onChange", "onClear", "onConfirm"]);
    const type = useMemo(() => {
        return getDataType(data);
    }, [data]);
    const steps = useMemo(() => {
        return Object.assign({
            year: 1,
            month: 1,
            day: 1,
            hour: 1,
            minute: 1,
            second: 1
        }, step);
    }, [step]);
    const fixedData = useMemo(() => {
        return getFixedData(data, steps);
    }, [data, steps]);
    const defaultValueRef = useRef(defaultValue);
    const defaultPickerValue = useMemo(() => {
        if (type === DATA_TYPE.MULTI || type === DATA_TYPE.TIME) {
            const _defaultValue = defaultValueRef.current;
            let defaultMultiValue = [];
            if (type === DATA_TYPE.TIME) {
                if (typeof defaultValueRef.current === 'string') {
                    defaultMultiValue = _defaultValue.split(':').map((el) => parseInt(el));
                }
                else if (isArray(_defaultValue)) {
                    _defaultValue && (defaultMultiValue = _defaultValue);
                }
            }
            else {
                defaultMultiValue = defaultValueRef.current;
            }
            return fixedData.map((el, index) => {
                if (defaultMultiValue && defaultMultiValue[index]) {
                    return (el.find((cl) => {
                        return cl.text === defaultMultiValue[index] || cl.value === defaultMultiValue[index];
                    }) || el[0]);
                }
                else {
                    return el[0];
                }
            }, []);
        }
        else if (type === DATA_TYPE.TREE || type === DATA_TYPE.REGION) {
            const _defaultValue = defaultValueRef.current;
            const defaultTreeValue = [];
            const loop = (child, zIndex) => {
                const row = child.find((el) => {
                    return (_defaultValue &&
                        _defaultValue[zIndex] &&
                        (el.value === _defaultValue[zIndex] || el.text === _defaultValue[zIndex]));
                });
                if (row) {
                    defaultTreeValue[zIndex] = row;
                    if (row.children) {
                        loop(row.children, zIndex + 1);
                    }
                }
                else {
                    defaultTreeValue[zIndex] = child[0];
                    if (child[0].children) {
                        loop(child[0].children, zIndex + 1);
                    }
                }
            };
            loop(fixedData, 0);
            return defaultTreeValue;
        }
        else if (type === DATA_TYPE.DATE) {
            let defaultDate;
            const _defaultValue = defaultValueRef.current;
            if (_defaultValue) {
                defaultDate = convertDate(_defaultValue);
                if (!dateCols.includes('year')) {
                    defaultDate = initDateYear(defaultDate);
                }
            }
            else {
                defaultDate = new Date();
            }
            if (dateRange) {
                if (dateRange[0]) {
                    dateRange[0] = convertDate(dateRange[0]);
                    if (!dateCols.includes('year')) {
                        dateRange[0] = initDateYear(dateRange[0]);
                    }
                    if (defaultDate.getTime() < dateRange[0].getTime()) {
                        defaultDate = convertDate(dateRange[0]);
                    }
                }
                if (dateRange[1]) {
                    dateRange[1] = convertDate(dateRange[1]);
                    if (!dateCols.includes('year')) {
                        dateRange[1] = initDateYear(dateRange[1]);
                    }
                    if (defaultDate.getTime() > dateRange[1].getTime()) {
                        defaultDate = convertDate(dateRange[1]);
                    }
                }
            }
            const defaultDatePickerValue = {};
            if (dateCols.includes('year')) {
                defaultDatePickerValue.year = convertTextToRecord(defaultDate.getFullYear(), '年');
            }
            if (dateCols.includes('month')) {
                defaultDatePickerValue.month = convertTextToRecord(defaultDate.getMonth() + 1, '月');
            }
            if (dateCols.includes('day')) {
                defaultDatePickerValue.day = convertTextToRecord(defaultDate.getDate(), '日');
            }
            return defaultDatePickerValue;
        }
        else if (type === DATA_TYPE.DATE_RANGE) {
            let defaultDate;
            const _defaultValue = defaultValueRef.current;
            if (_defaultValue) {
                defaultDate = _defaultValue.map((el) => {
                    return convertDate(el);
                });
                if (!dateCols.includes('year')) {
                    defaultDate = defaultDate.map((el) => {
                        return initDateYear(el);
                    });
                }
            }
            else {
                defaultDate = [new Date(), new Date()];
            }
            if (dateRange) {
                if (dateRange[0]) {
                    dateRange[0] = convertDate(dateRange[0]);
                    if (!dateCols.includes('year')) {
                        dateRange[0] = initDateYear(dateRange[0]);
                    }
                    if (defaultDate[0].getTime() < dateRange[0].getTime()) {
                        defaultDate[0] = convertDate(dateRange[0]);
                    }
                    if (defaultDate[1].getTime() < dateRange[0].getTime()) {
                        defaultDate[1] = convertDate(dateRange[0]);
                    }
                }
                if (dateRange[1]) {
                    dateRange[1] = convertDate(dateRange[1]);
                    if (!dateCols.includes('year')) {
                        dateRange[1] = initDateYear(dateRange[1]);
                    }
                    if (defaultDate[0].getTime() > dateRange[1].getTime()) {
                        defaultDate[0] = convertDate(dateRange[1]);
                    }
                    if (defaultDate[1].getTime() > dateRange[1].getTime()) {
                        defaultDate[1] = convertDate(dateRange[1]);
                    }
                }
            }
            const defaultDateRangePickerValue = [{}, {}];
            if (dateCols.includes('year')) {
                defaultDateRangePickerValue[0].year = convertTextToRecord(defaultDate[0].getFullYear(), '年');
                defaultDateRangePickerValue[1].year = convertTextToRecord(defaultDate[1].getFullYear(), '年');
            }
            if (dateCols.includes('month')) {
                defaultDateRangePickerValue[0].month = convertTextToRecord(defaultDate[0].getMonth() + 1, '月');
                defaultDateRangePickerValue[1].month = convertTextToRecord(defaultDate[1].getMonth() + 1, '月');
            }
            if (dateCols.includes('day')) {
                defaultDateRangePickerValue[0].day = convertTextToRecord(defaultDate[0].getDate(), '日');
                defaultDateRangePickerValue[1].day = convertTextToRecord(defaultDate[1].getDate(), '日');
            }
            return defaultDateRangePickerValue;
        }
        else {
            const _defaultValue = defaultValueRef.current;
            if (!isEmpty(_defaultValue)) {
                return (fixedData.find((cl) => {
                    return cl.text === _defaultValue || cl.value === _defaultValue;
                }) || fixedData[0]);
            }
            else {
                return fixedData[0];
            }
        }
    }, [dateCols, dateRange, fixedData, type]);
    const [pickerValue, setPickerValue] = useState(defaultPickerValue);
    const handleChange = useCallback((value, index) => {
        setPickerValue(value);
        onChange && onChange(value, index);
    }, [onChange, setPickerValue]);
    const handleConfirm = useCallback(() => {
        onConfirm && onConfirm(pickerValue);
    }, [onConfirm, pickerValue]);
    return (React.createElement("div", Object.assign({ className: prefixCls() }, restProps),
        React.createElement("div", { className: prefixCls('header') },
            React.createElement("div", { className: prefixCls('cancel'), onClick: onCancel }, "\u53D6\u6D88"),
            title && React.createElement("div", { className: prefixCls('title') }, title),
            React.createElement("div", { className: prefixCls('right') },
                allowClear && (React.createElement("div", { className: prefixCls('clear'), onClick: onClear }, "\u6E05\u9664")),
                React.createElement("div", { className: prefixCls('sure'), onClick: handleConfirm }, "\u786E\u5B9A"))),
        useGetCols({
            type,
            data: fixedData,
            defaultValue: pickerValue,
            dateRange,
            dateCols,
            step: steps,
            onChange: handleChange
        })));
};
export default PickerView;
